import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import {
  LogoEditorial,
  EditorialMark,
  LogoMonogram,
  MonogramMark,
  LogoCrystal,
  CrystalMark,
} from "../brand/LogoDirections";

const PALETTE = [
  { name: "Ink", value: "#0F0F0F" },
  { name: "Bone", value: "#F5F5F0" },
  { name: "Terracotta", value: "#C85A40" },
  { name: "Forest", value: "#2C4C3B" },
];

const Direction = ({
  letter,
  title,
  subtitle,
  description,
  pros,
  considerations,
  Lockup,
  Mark,
  testId,
}) => (
  <article
    data-testid={testId}
    className="border-t border-hairline pt-14 md:pt-20"
  >
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 mb-12">
      <div className="lg:col-span-5">
        <div className="flex items-baseline gap-4 mb-4">
          <span className="font-wordmark italic font-semibold text-terracotta text-6xl leading-none">
            {letter}
          </span>
          <div>
            <div className="overline text-inkmute">Direction {letter}</div>
            <h2 className="font-display font-black text-3xl md:text-4xl text-ink tracking-tight leading-[1.05]">
              {title}
            </h2>
          </div>
        </div>
        <p className="text-ink/75 text-base md:text-lg leading-relaxed mt-4">
          {subtitle}
        </p>
        <p className="text-inkmute text-sm md:text-base mt-4 leading-relaxed">
          {description}
        </p>

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div>
            <span className="overline text-terracotta block mb-3">Strengths</span>
            <ul className="space-y-2">
              {pros.map((p, i) => (
                <li key={i} className="text-sm text-ink/80 leading-relaxed flex gap-2">
                  <span className="text-terracotta shrink-0">✦</span>
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <span className="overline text-ink block mb-3">Consider</span>
            <ul className="space-y-2">
              {considerations.map((c, i) => (
                <li key={i} className="text-sm text-ink/80 leading-relaxed flex gap-2">
                  <span className="text-inkmute shrink-0">·</span>
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className="lg:col-span-7 space-y-4">
        {/* Lockup on light */}
        <div
          className="bg-bone border border-hairline rounded-sm px-8 py-12 flex items-center justify-center text-ink"
          data-testid={`${testId}-lockup-light`}
        >
          <Lockup size={360} />
        </div>
        {/* Lockup on dark */}
        <div
          className="bg-ink rounded-sm px-8 py-12 flex items-center justify-center text-bone"
          data-testid={`${testId}-lockup-dark`}
        >
          <Lockup size={360} />
        </div>
        {/* Lockup on forest */}
        <div
          className="rounded-sm px-8 py-12 flex items-center justify-center text-bone"
          style={{ backgroundColor: "#2C4C3B" }}
          data-testid={`${testId}-lockup-forest`}
        >
          <Lockup size={360} />
        </div>
      </div>
    </div>

    {/* Scale row */}
    <div className="bg-surfacealt border border-hairline rounded-sm p-6 md:p-8 mb-4">
      <div className="overline text-ink mb-6">Scale & application</div>
      <div className="flex flex-wrap items-end gap-10 md:gap-14">
        <div className="flex flex-col items-center gap-2 text-ink">
          <Mark size={96} />
          <span className="text-xs text-inkmute">Avatar · 96px</span>
        </div>
        <div className="flex flex-col items-center gap-2 text-ink">
          <Mark size={48} />
          <span className="text-xs text-inkmute">App icon · 48px</span>
        </div>
        <div className="flex flex-col items-center gap-2 text-ink">
          <Mark size={24} />
          <span className="text-xs text-inkmute">Favicon · 24px</span>
        </div>
        <div className="flex flex-col items-center gap-2 ml-auto text-bone bg-ink p-4 rounded-sm">
          <Mark size={64} color="#F5F5F0" />
          <span className="text-xs text-bone/65">On ink</span>
        </div>
        <div className="flex flex-col items-center gap-2 text-bone bg-terracotta p-4 rounded-sm">
          <Mark size={64} color="#F5F5F0" accent="#0F0F0F" />
          <span className="text-xs text-bone/85">On accent</span>
        </div>
      </div>
    </div>
  </article>
);

export default function LogoStudio() {
  return (
    <main data-testid="logo-studio-page" className="min-h-screen bg-bone text-ink">
      {/* Top bar */}
      <div className="border-b border-hairline">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-6 flex items-center justify-between">
          <Link
            to="/"
            data-testid="logo-studio-back"
            className="inline-flex items-center gap-2 overline text-ink hover:text-terracotta link-underline"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to site
          </Link>
          <span className="overline text-inkmute">Brand · Logo Directions</span>
        </div>
      </div>

      {/* Header */}
      <header className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 pt-16 md:pt-24 pb-12">
        <div className="overline text-terracotta mb-4">Logo Studio</div>
        <h1 className="font-display font-black text-5xl md:text-7xl tracking-tight leading-[1.02] max-w-4xl">
          Three directions,
          <br />
          <span className="font-wordmark italic font-semibold text-terracotta">
            same brand DNA.
          </span>
        </h1>
        <p className="mt-6 text-ink/75 text-lg md:text-xl leading-relaxed max-w-2xl">
          All three use the site's actual palette — bone, ink, terracotta, forest —
          so you can see them living next to the products and the dark hero in
          one consistent system.
        </p>

        {/* Palette swatches */}
        <div className="mt-10 flex flex-wrap gap-3" data-testid="palette-swatches">
          {PALETTE.map((c) => (
            <div
              key={c.name}
              className="flex items-center gap-3 border border-hairline rounded-sm px-3 py-2 bg-surface"
            >
              <span
                className="w-6 h-6 rounded-sm border border-hairline"
                style={{ backgroundColor: c.value }}
              />
              <span className="overline text-ink">{c.name}</span>
              <span className="text-xs text-inkmute font-mono">{c.value}</span>
            </div>
          ))}
        </div>
      </header>

      <section className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 pb-24 md:pb-32 space-y-20 md:space-y-28">
        <Direction
          letter="A"
          title="Editorial Wordmark"
          subtitle="Pure typography. A Playfair italic ‘Thrive’ with a single terracotta micro-dot replacing the dot on the i, and a Swiss-spaced ‘BY MADSEN’ below."
          description="Most premium, least busy. Leans into the editorial wellness positioning we built the site around. Works equally well embossed on packaging or printed on a hangtag. The ‘T·’ mark gives you a strong favicon."
          pros={[
            "Reads as a luxury food/wellness brand, not a gym brand",
            "Scales perfectly — typography never collapses",
            "Easiest to evolve into Phase 02 packaging system",
            "Already proven on the live site",
          ]}
          considerations={[
            "No standalone illustrative mark — relies on the wordmark",
            "Needs Playfair Display licensed for print",
          ]}
          Lockup={LogoEditorial}
          Mark={EditorialMark}
          testId="direction-a"
        />

        <Direction
          letter="B"
          title="TM Monogram Cartouche"
          subtitle="A sharp, architectural ‘T’ inside a thin-rule square with a terracotta slab beneath — the Madsen surname signed off as a micro ‘m’. Pairs with a bold Outfit wordmark."
          description="The most ‘brand identity system’ option. Gives you a strong standalone mark that survives at any size, looks at home on a bottle cap, an Instagram avatar, a polo shirt embroidery. Architectural, considered, masculine but not aggressive."
          pros={[
            "Strongest standalone mark of the three",
            "Mono-color version works for embroidery, embossing, foil",
            "Cartouche feels like a quality seal / hallmark",
            "Clearly attributable to Madsen (the family name)",
          ]}
          considerations={[
            "More ‘corporate / heritage’ — less editorial",
            "Slightly cooler register than Direction A",
          ]}
          Lockup={LogoMonogram}
          Mark={MonogramMark}
          testId="direction-b"
        />

        <Direction
          letter="C"
          title="Mineral Crystal Mark"
          subtitle="A faceted crystal — read it as a salt mineral, a grain of shirataki, a single facet of nutrition. Sits beside the wordmark like a hallmark."
          description="The most narrative option. Ties the mark directly to the product line: electrolytes and grains. The faceted shape gives you a hero motif you can blow up across packaging, hero images, and Instagram. Closest in spirit to the figure logo you shared — but stripped of cliché."
          pros={[
            "Symbolic of the actual products (minerals + grains)",
            "Dynamic — facets can be rotated/repeated as a pattern",
            "Visually distinctive in a category full of wordmarks",
            "Premium when used with the terracotta facet only",
          ]}
          considerations={[
            "Slightly more decorative — easier to date",
            "Needs careful proportion at very small sizes",
          ]}
          Lockup={LogoCrystal}
          Mark={CrystalMark}
          testId="direction-c"
        />

        {/* Comparison row */}
        <section
          data-testid="side-by-side-comparison"
          className="border-t border-hairline pt-12"
        >
          <div className="overline text-terracotta mb-3">Side-by-side</div>
          <h3 className="font-display font-black text-3xl md:text-4xl text-ink tracking-tight mb-8">
            The three lockups, same surface.
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-bone border border-hairline rounded-sm p-8 flex items-center justify-center text-ink min-h-[200px]">
              <LogoEditorial size={260} />
            </div>
            <div className="bg-bone border border-hairline rounded-sm p-8 flex items-center justify-center text-ink min-h-[200px]">
              <LogoMonogram size={300} />
            </div>
            <div className="bg-bone border border-hairline rounded-sm p-8 flex items-center justify-center text-ink min-h-[200px]">
              <LogoCrystal size={300} />
            </div>
            <div className="bg-ink rounded-sm p-8 flex items-center justify-center text-bone min-h-[200px]">
              <LogoEditorial size={260} />
            </div>
            <div className="bg-ink rounded-sm p-8 flex items-center justify-center text-bone min-h-[200px]">
              <LogoMonogram size={300} />
            </div>
            <div className="bg-ink rounded-sm p-8 flex items-center justify-center text-bone min-h-[200px]">
              <LogoCrystal size={300} />
            </div>
          </div>
        </section>

        {/* Decision helper */}
        <section
          data-testid="decision-helper"
          className="bg-ink text-bone rounded-sm p-8 md:p-12"
        >
          <div className="overline text-terracotta mb-4">My recommendation</div>
          <p className="font-wordmark italic text-bone text-3xl md:text-4xl leading-snug max-w-3xl">
            “If forced to pick: <span className="text-terracotta">B</span> for
            packaging credibility, <span className="text-terracotta">A</span> for
            the website you already have. Skip C unless you’re committed to a
            visual pattern system.”
          </p>
          <p className="mt-6 text-bone/70 max-w-2xl leading-relaxed">
            B gives you the strongest standalone mark — important once you’re
            printing on labels, bottles, and pouches. A keeps the editorial
            wellness register the site lives in. The two can also coexist —
            wordmark on the site, monogram on packaging.
          </p>
          <Link
            to="/"
            data-testid="decision-back-home"
            className="mt-8 inline-flex items-center gap-2 bg-terracotta hover:bg-bone hover:text-ink transition-colors duration-300 text-white px-6 py-3.5 rounded-sm font-display font-semibold text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to the site
          </Link>
        </section>
      </section>
    </main>
  );
}
